// Header.jsx



import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import { useAuth } from '../../hooks/useAuth';
import HeaderStyles from './Header.styles';

const navItems = [
  { label: 'Home', path: 'https://mytenantaccess.com' },
  // { label: 'Rental Managers', path: '/rental-managers' },
  // { label: 'Smart Locks & More', path: '/smart-locks' },
  { label: 'Tour Rentals', path: '/listings' },
  { label: 'Contact Us', path: 'https://www.mytenantaccess.com/contact' },
];

const Header = () => {
  const { currentUser, fetchUserType, logout } = useAuth();
  const [userType, setUserType] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch user type on mount and when currentUser changes
  useEffect(() => {
    if (!currentUser) {
      setUserType(null);
      return;
    }
    const getUserType = async () => {
      try {
        const type = await fetchUserType(currentUser.uid);
        setUserType(type);
      } catch (error) {
        setUserType(null);
        console.error('Error fetching user type:', error);
      }
    };
    getUserType();
  }, [currentUser, fetchUserType]);

  const handleLogout = async () => {
    await logout();
    setUserType(null);
    navigate('/login');
  };

  const dashboardPath = () => {
    if (userType === 'landlord') return '/agent-dashboard';
    if (userType === 'renter') return '/user-dashboard';
    return null;
  };

  // Drawer for mobile navigation
  const renderDrawer = () => (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: HeaderStyles.mobileDrawer,
        'aria-label': 'site navigation',
      }}
    >
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={() => setDrawerOpen(false)}
              sx={HeaderStyles.mobileListItem}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={HeaderStyles.mobileDrawerText}
              />
            </ListItemButton>
          </ListItem>
        ))}
        {currentUser && dashboardPath() && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={dashboardPath()}
              onClick={() => setDrawerOpen(false)}
              sx={HeaderStyles.mobileListItem}
            >
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          {currentUser ? (
            <ListItemButton
              onClick={() => {
                setDrawerOpen(false);
                handleLogout();
              }}
              sx={{
                ...HeaderStyles.mobileListItem,
                color: theme.palette.error.main,
                fontWeight: 'bold',
              }}
            >
              <ListItemText primary="Sign Out" />
            </ListItemButton>
          ) : (
            <ListItemButton
              component={Link}
              to="/login"
              onClick={() => setDrawerOpen(false)}
              sx={{
                ...HeaderStyles.mobileListItem,
                color: theme.palette.primary.main,
                fontWeight: 'bold',
              }}
            >
              <ListItemText primary="Login" />
            </ListItemButton>
          )}
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <Box>
      {/* Top contact bar (desktop only) */}
      {!isMobile && (
        <Box sx={HeaderStyles.contactBar}>
          <Box sx={HeaderStyles.contactItem}>
            <PhoneIcon sx={{ color: '#81c241', fontSize: '1rem', mr: 1 }} />
            <Typography variant="body2" sx={HeaderStyles.contactText}>
              1&nbsp;(800)&nbsp;346-8999
            </Typography>
          </Box>
          <Box sx={HeaderStyles.contactItem}>
            <EmailIcon sx={{ color: '#81c241', fontSize: '1rem', mr: 1 }} />
            <Typography variant="body2" sx={HeaderStyles.contactText}>
              info@mytenantaccess.com
            </Typography>
          </Box>
        </Box>
      )}

      {/* Main Navigation */}
      <AppBar position="static" sx={HeaderStyles.appBar} elevation={1}>
        <Toolbar sx={HeaderStyles.toolbar}>
          {/* Logo */}
          <Box sx={HeaderStyles.logoContainer}>
            <Link to="/">
              <Box
                component="img"
                src="/header_logo.png"
                alt="Tenant Guided Tours"
                sx={HeaderStyles.logo}
              />
            </Link>
          </Box>

          {isMobile ? (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
              {renderDrawer()}
            </>
          ) : (
            <>
              <Box sx={HeaderStyles.navLinks}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    component={Link}
                    to={item.path}
                    sx={HeaderStyles.link}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              <Box sx={HeaderStyles.authButtons}>
                {currentUser ? (
                  <>
                    {dashboardPath() && (
                      <Button
                        component={Link}
                        to={dashboardPath()}
                        sx={HeaderStyles.dashboardButton}
                      >
                        Dashboard
                      </Button>
                    )}
                    <Button
                      onClick={handleLogout}
                      sx={HeaderStyles.logoutButton}
                      color="error"
                      variant="outlined"
                    >
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button
                    component={Link}
                    to="/login"
                    sx={HeaderStyles.loginButton}
                    color="primary"
                    variant="contained"
                  >
                    Login
                  </Button>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Header;
